import { Component, computed, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { UiKitModule } from "@app/ui-kit.module";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { ModalRef } from "@odx/angular/components/modal";
import { MonitoringGroupedPartsTableComponent } from "@project/monitoring/grouped-parts/monitoring-grouped-parts-table.component";
import { getMonitoringOtherProductsLabel } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartsService } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.service";
import {
  MonitoringGroupedPartRow,
  MonitoringGroupedPartSection,
  MonitoringGroupedPartSourceItem,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.types";
import { MonitoringOverviewComponent } from "@project/monitoring/grouped-parts/monitoring-overview.component";
import { of } from "rxjs";

type MonitoringItemDetailsModalData = {
  items: MonitoringGroupedPartSourceItem[];
  category?: MonitoringCategory;
  title: string;
  showCategory?: boolean;
  hideUnavailable?: boolean;
};

@Component({
  selector: "app-floorplan-monitoring-item-details-modal",
  imports: [UiKitModule, MonitoringOverviewComponent, MonitoringGroupedPartsTableComponent],
  templateUrl: "./floorplan-monitoring-item-details-modal.component.html",
  styleUrl: "./floorplan-monitoring-item-details-modal.component.scss",
})
export class FloorplanMonitoringItemDetailsModalComponent {
  public readonly modalRef = inject(ModalRef);
  private readonly groupedPartsService = inject(MonitoringGroupedPartsService);
  private readonly modalData = this.modalRef.data as MonitoringItemDetailsModalData;
  private readonly sourceItems = this.modalData.items ?? [];
  private readonly resolvedCategory = this.modalData.category ?? MonitoringCategory.PATIENT_MONITORS;
  private readonly isClosing = signal(false);

  protected readonly title = this.modalData.title;
  protected readonly otherProductsLabel = getMonitoringOtherProductsLabel();
  protected readonly showCategory = this.modalData.showCategory ?? true;
  private readonly hideUnavailable = this.modalData.hideUnavailable ?? false;
  private readonly resolvedSections = toSignal<MonitoringGroupedPartSection[] | undefined>(
    this.sourceItems.length
      ? this.groupedPartsService.buildSections(
          this.sourceItems,
          this.resolvedCategory,
          this.otherProductsLabel,
          this.hideUnavailable,
        )
      : of([]),
    { initialValue: undefined },
  );
  protected readonly summarySections = computed(() => {
    return this.isClosing() ? [] : (this.resolvedSections() ?? []);
  });
  protected readonly tableRows = computed<MonitoringGroupedPartRow[]>(() => {
    return this.summarySections().flatMap((section) => section.rows);
  });
  protected readonly isLoading = computed(() => {
    return !this.isClosing() && this.sourceItems.length > 0 && this.resolvedSections() === undefined;
  });

  onConfirm() {
    this.isClosing.set(true);
    this.modalRef.close(true);
  }
}
